import { useState, useCallback, useEffect, useRef } from "react";
import { useUser, useSession } from "@clerk/clerk-react";
import axios from "axios";
import { getUnexpiredToken, logAxiosError, useUserId } from "@/utils/utils";
import { track } from "@/utils/events/tracker";
import { fetchUserRepos, GitProvider } from "@/utils/gitProviderUtils";

const DEFAULT_PERMISSION_INSTALLATION_LINK =
  "https://github.com/apps/tempo-labs-ai/installations/new";

interface GitHubPermissionState {
  status: "idle" | "checking" | "granted" | "denied" | "error";
  reason: string | null;
  link: string | null;
  githubUserId?: string;
}

interface GitRepo {
  name: string;
  id?: number | string;
  full_name?: string;
  path_with_namespace?: string;
  owner?: {
    login: string;
    avatar_url: string;
  };
}

interface GitRepoState {
  repos: GitRepo[];
  loading: boolean;
  error: string | null;
}

interface GitHubInstallation {
  target_id: number;
  target_type: string;
  permissions: {
    administration?: string;
    [key: string]: string | undefined;
  };
  html_url?: string;
}

interface GitHubAccount {
  provider: string;
  providerUserId: string;
}

interface GitLabRepo {
  path_with_namespace: string;
  id: number | string;
  namespace: {
    name: string;
    path: string;
  };
}

interface GitHubRepo {
  full_name: string;
  owner: {
    login: string;
    avatar_url: string;
  };
}

export const useGitProvider = (provider: GitProvider = GitProvider.GITHUB) => {
  const { user } = useUser();
  const { session } = useSession();
  const userId = useUserId();

  const [githubPermissionState, setGithubPermissionState] =
    useState<GitHubPermissionState>({
      status: "idle",
      reason: null,
      link: null,
    });

  const [repoState, setRepoState] = useState<GitRepoState>({
    repos: [],
    loading: false,
    error: null,
  });

  const [pollCount, setPollCount] = useState(0);
  const [isPolling, setIsPolling] = useState(false);
  const maxPollAttempts = 12; // 12 * 5s = 1 minute
  const pollInterval = 5000;

  const checkGithubPermissions = useCallback(
    async (forceCheck = false) => {
      // Only check permissions for GitHub
      if (provider && provider !== GitProvider.GITHUB) {
        return;
      }

      // Check if user is available
      if (!user) {
        setGithubPermissionState({ status: "idle", reason: null, link: null });
        return;
      }

      const githubProviderUserId = user.externalAccounts?.find(
        (account: GitHubAccount) => account.provider === "github"
      )?.providerUserId;

      if (!githubProviderUserId) {
        setGithubPermissionState({
          status: "denied",
          reason:
            "Tempo GitHub App installation not found for your user account (no GitHub ID).",
          link: DEFAULT_PERMISSION_INSTALLATION_LINK,
          githubUserId: githubProviderUserId,
        });
        return;
      }

      setGithubPermissionState((prev) => {
        // Only skip the check if not forcing and we already have granted permission for this user
        if (
          !forceCheck &&
          prev.status === "granted" &&
          prev.githubUserId === githubProviderUserId
        ) {
          return prev; // No state change, prevents re-execution
        }

        return {
          ...prev,
          status: "checking",
          githubUserId: githubProviderUserId,
        };
      });

      try {
        if (!session) {
          setGithubPermissionState({
            status: "error",
            reason: "Session not available.",
            link: null,
            githubUserId: githubProviderUserId,
          });
          return;
        }

        const supabaseJWT = await getUnexpiredToken(session);
        const { data } = await axios.get(
          `${
            import.meta.env.VITE_BACKEND_BASE_URL
          }/gitProvider/user/installations`,
          { headers: { Authorization: `Bearer ${supabaseJWT}` } }
        );

        track("CHECK_GITHUB_INSTALLATIONS", { data, userId });
        const { installations } = data;

        track("GITHUB_INSTALLATIONS_CHECK", {
          githubProviderUserId,
          userId,
          installationsCount: installations?.length || 0,
          installations: installations?.map((i: GitHubInstallation) => ({
            target_id: i.target_id,
            target_type: i.target_type,
            permissions: i.permissions,
          })),
        });

        // Check for installations - both personal and organization
        const validInstallation = installations.find(
          (installation: GitHubInstallation) => {
            // Check if user has administration write permission on any installation
            // This includes both personal installations and organization installations where the user has access
            return installation.permissions?.administration === "write";
          }
        );

        if (validInstallation) {
          setGithubPermissionState({
            status: "granted",
            reason: null,
            link: null,
            githubUserId: githubProviderUserId,
          });
        } else {
          // Check if there are any installations but without proper permissions
          const installationWithoutPermission = installations.find(
            (installation: GitHubInstallation) => {
              const targetId = installation.target_id;
              const userIdStr = String(githubProviderUserId);
              const targetIdStr = String(targetId);
              return userIdStr === targetIdStr;
            }
          );

          if (installationWithoutPermission) {
            setGithubPermissionState({
              status: "denied",
              reason: "Missing: 'administration' write permission.",
              link:
                installationWithoutPermission.html_url ||
                DEFAULT_PERMISSION_INSTALLATION_LINK,
              githubUserId: githubProviderUserId,
            });
          } else {
            setGithubPermissionState({
              status: "denied",
              reason:
                "Tempo GitHub App installation not found for your user account or organizations.",
              link: DEFAULT_PERMISSION_INSTALLATION_LINK,
              githubUserId: githubProviderUserId,
            });
          }
        }
      } catch (error) {
        logAxiosError("Failed to check permissions", error);
        setGithubPermissionState({
          status: "error",
          reason: "Error checking permissions.",
          link: DEFAULT_PERMISSION_INSTALLATION_LINK,
          githubUserId: githubProviderUserId,
        });
      }
    },
    [user, Boolean(session), provider]
  );

  const fetchRepos = useCallback(
    async (targetProvider?: GitProvider) => {
      const activeProvider = targetProvider || provider || GitProvider.GITHUB;

      if (!session) {
        setRepoState((prev) => ({ ...prev, error: "Session not available" }));
        return;
      }

      setRepoState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const supabaseJWT = await getUnexpiredToken(session);
        if (!supabaseJWT) {
          setRepoState((prev) => ({
            ...prev,
            loading: false,
            error: "Could not get authentication token",
          }));
          return;
        }

        const rawRepos = await fetchUserRepos({
          token: supabaseJWT,
          provider: activeProvider,
        });

        let mappedRepos: GitRepo[];
        if (activeProvider === GitProvider.GITLAB) {
          mappedRepos = rawRepos.map((repo: GitLabRepo) => ({
            name: repo.path_with_namespace,
            id: repo.id,
            path_with_namespace: repo.path_with_namespace,
            namespace: repo.namespace,
          }));
        } else {
          mappedRepos = rawRepos.map((repo: GitHubRepo) => ({
            name: repo.full_name,
            full_name: repo.full_name,
            owner: repo.owner,
          }));
        }

        setRepoState({
          repos: mappedRepos,
          loading: false,
          error: null,
        });
      } catch (err) {
        logAxiosError("Failed to fetch repos", err);
        setRepoState({
          repos: [],
          loading: false,
          error: "Failed to fetch repositories",
        });
      }
    },
    [Boolean(session), provider]
  );

  const startPolling = useCallback(() => {
    setIsPolling(true);
    setPollCount(0);
    checkGithubPermissions(true);
  }, [checkGithubPermissions]);

  const resetPermissionState = useCallback(() => {
    setGithubPermissionState({ status: "idle", reason: null, link: null });
    setIsPolling(false);
    setPollCount(0);
  }, []);

  const resetRepoState = useCallback(() => {
    setRepoState({ repos: [], loading: false, error: null });
  }, []);

  // Polling effect for GitHub permissions
  useEffect(() => {
    let intervalId: NodeJS.Timeout | undefined;
    if (isPolling && provider === GitProvider.GITHUB) {
      if (
        pollCount < maxPollAttempts &&
        githubPermissionState.status !== "granted"
      ) {
        intervalId = setInterval(() => {
          checkGithubPermissions(true);
          setPollCount((prevCount) => prevCount + 1);
        }, pollInterval);
      } else {
        setIsPolling(false);
        if (
          pollCount >= maxPollAttempts &&
          githubPermissionState.status !== "granted"
        ) {
          // Could emit a timeout event here if needed
        }
        setPollCount(0);
      }
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [
    isPolling,
    pollCount,
    githubPermissionState.status,
    checkGithubPermissions,
    provider,
  ]);

  // Auto-fetch repos when session/user/provider changes
  useEffect(() => {
    if (session && user && provider) {
      fetchRepos(provider);
    }
  }, [Boolean(session), Boolean(user), provider, fetchRepos]);

  return {
    // Permission-related (GitHub only)
    githubPermissionState,
    checkGithubPermissions,
    startPolling,
    resetPermissionState,
    isPolling,
    isPermissionGranted: githubPermissionState.status === "granted",

    // Repo-related (both providers)
    repoState,
    fetchRepos,
    resetRepoState,

    // Convenience getters
    repos: repoState.repos,
    reposLoading: repoState.loading,
    reposError: repoState.error,
  };
};
